// Taşeron portalı — Belge Analizi inceleme paneli: tek-sayfa önizleme + carousel (plan S25 C5,
// `docs/plans/S25-analysis-inbox.md`; tasarım incelemesi `docs/reviews/S25-inbox-design.md`
// §2.5/§3.4). `DocumentAnalysisPreviewStrip.tsx` C14'te SİLİNDİ — bu dosya o zamana kadar eski
// `DocumentAnalysisItemCard`'ın kullandığı ÜÇ mekanizmayı LİFT etmişti, tek-sayfa carousel
// biçimine uyarlanmış olarak:
//   1) süresi dolmuş presigned URL koruması — `<img onError>` → ÇAĞIRAN tarafın (`onThumbnailExpired`)
//      TEK seferlik guard'ı (bu bileşen kendi guard'ını TUTMAZ, yalnız tetikler).
//   2) sayfa başına `errored` kümesi (şerit yerine tek görünür sayfa + lightbox aynı kümeyi paylaşır).
//   3) lightbox odak iadesi (`useReturnFocus`, `Modal`'ın `Dialog.Trigger` TAŞIMAMASının telafisi).
//
// **Asla tüm sayfalar aynı anda gösterilmez (C5)** — `pages` zaten kalem başına süzülmüş/sıralı
// gelir (`lib/documentAnalysisPages.ts`, ÇAĞIRAN sayfa düzeyinde hesaplar); bu bileşen `pages`'i
// İKİNCİ kez filtrelemez, yalnız `index` durumuyla TEK birini gösterir.
//
// `thumbnailUrl` `null` olabilir (S3 yazımı başarısız) — bu bir HATA değildir, yer tutucu
// render edilir. Sayfa rasteri genişliği sabit bir piksel sayısı DEĞİL, doc-check'in
// `THUMBNAIL_WIDTH_PX` config'i (yakın zamanda 480'den yükseltildi) — büyütülmüş görünümü
// GERÇEKTEN okunur kılar; koyu temada ters çevirme/filtre YOK (kullanıcı AI'ın okuduğunu
// görsel olarak doğruluyor).

import { Button, Modal, cn } from '@isgportal/shared';
import type {
  DocumentAnalysisFields,
  DocumentAnalysisIsgEgitimValidUntil,
  DocumentAnalysisPageDto,
} from '@isgportal/shared';
import { ChevronLeft, ChevronRight, ImageOff, Maximize2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPageSpan } from '../lib/documentAnalysisPages';
import { useReturnFocus } from '../lib/useReturnFocus';
import { AnalysisExtractedFields } from './AnalysisExtractedFields';

/** Bir sayfa için kararlı anahtar — şerit/lightbox emsaliyle AYNI (`pageKey`, `DocumentAnalysisPreviewStrip.tsx`). */
function pageKey(page: DocumentAnalysisPageDto): string {
  return `${page.sourceDocumentUuid ?? ''}:${page.pageNumber}`;
}

export function DocumentAnalysisPreview({
  pages,
  fileName,
  onThumbnailExpired,
  fields,
  derived,
  derivedSourceOk,
  culture,
  droppedCount,
}: {
  /** Zaten kaleme göre süzülmüş, sayfa sırasında (bkz. dosya üstü yorum). */
  pages: DocumentAnalysisPageDto[];
  fileName: string;
  onThumbnailExpired: () => void;
  /** Lightbox'a çıkarılan-alan doğrulama paneli — TÜMÜ opsiyonel (S26 devamı). `fields`
   * VERİLMEZSE (bu bileşenin diğer/gelecekteki çağıranları) lightbox BUGÜNKÜ gibi yalnız
   * görsel kalır; panel `AnalysisReviewPane.tsx`'in `AnalysisExtractedFields`e ZATEN geçtiği
   * AYNI beş değeri birebir alır, burada İKİNCİ kez HESAPLANMAZ. */
  fields?: DocumentAnalysisFields;
  derived?: DocumentAnalysisIsgEgitimValidUntil | null;
  derivedSourceOk?: boolean;
  culture?: string;
  droppedCount?: number | null;
}) {
  const { t } = useTranslation();
  // Süresi dolmuş/bozuk her sayfa KENDİ anahtarıyla izlenir — çağıran bileşen (`AnalysisReviewPane`)
  // kalem değişince (`key={item.uuid}`) bu bileşeni REMOUNT eder, yani `errored`/`index` her yeni
  // kalemde doğal olarak sıfırlanır; ayrı bir reset efekti GEREKMEZ.
  //
  // F5 (S25 web güvenlik incelemesi) — anahtarın DEĞERİ `pageKey(page)` DEĞİL, o anahtarda
  // BAŞARISIZ OLAN `thumbnailUrl`. `AnalysisReviewPane`'in tek seferlik refetch'i (dosya üstü
  // notu) TAZE presigned URL'ler getirdiğinde `page.thumbnailUrl` DEĞİŞİR; bu değişimi
  // `errored.get(key) === page.thumbnailUrl` karşılaştırması KENDİLİĞİNDEN yakalar (eski URL
  // kayıtlıdır, yeni URL ona eşit DEĞİLDİR) — ayrı bir "temizle" efekti YAZILMAZ, bir seferlik
  // dış guard'a (`refetchedAnalysesRef`) DOKUNULMAZ, yeniden silahlanmaz.
  const [errored, setErrored] = useState<Map<string, string>>(new Map());
  const [index, setIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // `Modal`'ın `Dialog.Trigger`'ı YOK — bu app'teki TEK odak-geri-verme yolu (bkz. `useReturnFocus.ts`
  // dosya üstü notu). Büyüteç düğmesi bu bileşenin içinde SABİT tek tetikleyici — `.capture` DEĞİL
  // (satır başına değişen bir tetikleyici değil), `.ref` doğrudan bağlanır.
  const trigger = useReturnFocus<HTMLButtonElement>();

  function markErrored(key: string, url: string) {
    setErrored((prev) => {
      if (prev.get(key) === url) {
        return prev;
      }
      const next = new Map(prev);
      next.set(key, url);
      return next;
    });
    onThumbnailExpired();
  }

  // Sol/sağ ok tuşları — yalnız lightbox açıkken, pencere düzeyinde (odak `Dialog.Content`
  // içindeki HERHANGİ bir elemanda olabilir; bkz. `DocumentAnalysisPreviewStrip.tsx` dosya üstü notu).
  useEffect(() => {
    if (!lightboxOpen) {
      return;
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'ArrowLeft') {
        setIndex((i) => Math.max(0, i - 1));
      } else if (event.key === 'ArrowRight') {
        setIndex((i) => Math.min(pages.length - 1, i + 1));
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, pages.length]);

  if (pages.length === 0) {
    return (
      // Must-fix #5 (sign-off §3.4) — `max-w-[26rem]` iki-sütunlu düzendeki sol sütunun ÜST
      // sınırıyla (`minmax(18rem,26rem)`) AYNI: tek-sütun hâlde (panel dar bir konteynerdeyken)
      // önizleme kartın tüm genişliğini kaplayıp A4'ü ~800px'e büyütmesin diye.
      <div className="mx-auto flex aspect-[210/297] w-full max-w-[26rem] flex-col items-center justify-center gap-2 border border-dashed border-border-strong bg-surface-muted p-4 text-center">
        <ImageOff className="h-6 w-6 text-muted" aria-hidden="true" />
        <span className="text-sm text-muted">{t('analysis.preview.unavailable')}</span>
      </div>
    );
  }

  const page = pages[index] ?? pages[0] ?? null;
  const key = page ? pageKey(page) : '';
  const broken =
    page === null || page.thumbnailUrl === null || errored.get(key) === page.thumbnailUrl;
  const pageSpan = formatPageSpan(pages.map((p) => p.pageNumber));
  // Lightbox'ın sağ doğrulama paneli — TEK kapı `fields`in VERİLMİŞ olması (`DocumentAnalysisFields`
  // `Record`i asla `null`/`undefined` DEĞİLDİR, bkz. `documentAnalyses.ts:197`, yalnız bu prop
  // hiç GEÇİLMEMİŞ olabilir). `derived`/`droppedCount` kendileri `null` taşıyabildiğinden onlar
  // kapı OLAMAZ.
  const hasExtractedData =
    fields !== undefined &&
    (Object.keys(fields).length > 0 ||
      derived != null ||
      (droppedCount != null && droppedCount > 0));

  // `<img>` yalnız `!broken`ken render edilir, yani bu çağrıldığında `page.thumbnailUrl` HER
  // ZAMAN `string`dir (yukarıdaki `broken` `null` kolunu ZATEN elemiştir).
  function handleImageError() {
    if (page?.thumbnailUrl) {
      markErrored(key, page.thumbnailUrl);
    }
  }

  function closeLightbox(open: boolean) {
    if (!open) {
      setLightboxOpen(false);
      trigger.restore();
    }
  }

  function goPrev() {
    setIndex((i) => Math.max(0, i - 1));
  }
  function goNext() {
    setIndex((i) => Math.min(pages.length - 1, i + 1));
  }

  return (
    // Must-fix #5 (sign-off §3.4) — aynı `max-w-[26rem]` sınırı burada da: panel tek sütuna
    // düştüğünde (bkz. `AnalysisReviewPane.tsx` `@container` eşiği) bu bileşen kartın tüm
    // genişliğini almaz, iki-sütunlu düzendeki sol sütunun ÜST sınırında kalır — A4 önizleme
    // ~800px'e büyüyüp formu ekranın altına itmez.
    <div className="mx-auto flex w-full max-w-[26rem] flex-col gap-2">
      {broken ? (
        <div className="relative aspect-[210/297] w-full overflow-hidden border border-border bg-surface-muted p-1">
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
            <ImageOff className="h-6 w-6 text-muted" aria-hidden="true" />
            <span className="text-sm text-muted">{t('analysis.preview.unavailable')}</span>
          </div>
        </div>
      ) : (
        // Tek tıklama hedefi: A4 çerçevesinin KENDİSİ düğme (lightbox'u açan tetikleyici) —
        // önceden yalnız köşedeki Maximize2 buton tıklanabilirdi, artık görselin her yeri de.
        // İçerideki büyüteç `<span aria-hidden>` yalnız DEKORATİF bir afordans, buton İÇİNDE
        // ikinci bir buton DEĞİL.
        <button
          ref={trigger.ref}
          type="button"
          onClick={(event) => {
            trigger.capture(event.currentTarget);
            setLightboxOpen(true);
          }}
          className="relative block aspect-[210/297] w-full cursor-zoom-in overflow-hidden border border-border bg-surface-muted p-1 hover:border-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={t('analysis.preview.expandAria')}
        >
          <img
            src={page?.thumbnailUrl ?? undefined}
            // S24-D5 emsali: sayfa başına dosya adını tekrarlayan bir `alt` bir ekran okuyucu
            // için gürültüdür — açıklama alttaki başlıkta (tasarım incelemesi §6.6).
            alt=""
            className="h-full w-full object-contain"
            loading="lazy"
            decoding="async"
            onError={handleImageError}
          />
          {page ? (
            <span className="absolute bottom-2 left-2 bg-inverse px-2 py-0.5 text-xs text-inverse-fg">
              {t('analysis.preview.pageChip', { page: page.pageNumber })}
            </span>
          ) : null}
          <span
            aria-hidden="true"
            className="absolute right-2 top-2 bg-inverse p-1.5 text-inverse-fg"
          >
            <Maximize2 className="h-4 w-4" />
          </span>
        </button>
      )}

      {pages.length > 1 ? (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={index === 0}
            onClick={goPrev}
            aria-label={t('analysis.preview.modal.previous')}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <span className="text-sm tnum text-muted">
            {index + 1} / {pages.length}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={index === pages.length - 1}
            onClick={goNext}
            aria-label={t('analysis.preview.modal.next')}
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      ) : null}

      <p className={cn('truncate text-xs text-muted', pages.length <= 1 && 'text-center')}>
        {pageSpan ? t('analysis.preview.itemCaption', { fileName, span: pageSpan }) : fileName}
      </p>

      <Modal
        open={lightboxOpen}
        onOpenChange={closeLightbox}
        title={t('analysis.preview.modal.caption', { fileName, page: page?.pageNumber ?? 0 })}
        closeLabel={t('analysis.preview.modal.close')}
        size="full"
      >
        {/* `fields` verilmediyse (`hasExtractedData === false`) bu sarmalayıcının TEK etkisi
            aşağıdaki `@container`i taşımaktır — o da hiçbir `@[...]:` varyantı hiçbir yerde
            eşleşmediği için görsel olarak SIFIR etkilidir; düzen bugünküyle BİREBİR aynı kalır. */}
        <div className={cn('flex flex-col gap-4', hasExtractedData && '@container')}>
          <div
            className={cn(
              'flex flex-col gap-4',
              // Repo emsali `AnalysisReviewPane.tsx`'in iki-sütun eşiğiyle AYNI mekanizma
              // (`@container`, VIEWPORT değil bu Modal içeriğinin KENDİ genişliği) — farklı
              // sayısal eşik, çünkü burada sağ sütun sabit ~22rem'lik dar bir doğrulama
              // paneli, sol tarafın 43.5rem'lik minimum içerik gereksinimi YOK.
              hasExtractedData && '@[48rem]:flex-row @[48rem]:items-start',
            )}
          >
            <div className="flex min-w-0 flex-1 flex-col gap-3">
              <div className="flex items-center justify-center border border-border bg-surface-muted p-2">
                {broken ? (
                  <p className="p-8 text-sm text-muted">{t('analysis.preview.unavailable')}</p>
                ) : (
                  <img
                    src={page?.thumbnailUrl ?? undefined}
                    alt=""
                    className={cn(
                      'w-full object-contain',
                      // Panel STACK olduğunda (dar konteyner) görsel + alan panelinin toplam
                      // yüksekliği tek başlarına bile viewport'u aşabilir — modal'ı SAYFA
                      // düzeyinde kaydırmak (görev talimatı) yerine her ikisi de kendi `40vh`
                      // tavanında kalır; iki sütuna geçince (`@[48rem]`) yan yana OLDUKLARI
                      // için eski `14rem` payı yeniden yeterlidir.
                      hasExtractedData
                        ? 'max-h-[40vh] @[48rem]:max-h-[calc(100dvh-14rem)]'
                        : 'max-h-[calc(100dvh-14rem)]',
                    )}
                    onError={handleImageError}
                  />
                )}
              </div>
              {pages.length > 1 ? (
                <div className="flex items-center justify-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={index === 0}
                    onClick={goPrev}
                    aria-label={t('analysis.preview.modal.previous')}
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={index === pages.length - 1}
                    onClick={goNext}
                    aria-label={t('analysis.preview.modal.next')}
                  >
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              ) : null}
            </div>

            {hasExtractedData ? (
              // Doğrulama yan paneli (S26 devamı, görev talimatı) — kullanıcı görseli TERK
              // ETMEDEN modelin okuduğu değerleri doğrulasın diye. `AnalysisExtractedFields`
              // OLDUĞU GİBİ yeniden kullanılır (dosya üstü notu, `AnalysisExtractedFields.tsx`)
              // — ikinci bir liste/yüzde güven İCAT EDİLMEZ.
              <div className="w-full shrink-0 @[48rem]:w-[22rem]">
                <div className="max-h-[40vh] overflow-y-auto border border-border bg-surface p-3 @[48rem]:max-h-[calc(100dvh-14rem)]">
                  {/* `AnalysisExtractedFields` bu metni KENDİ İÇİNDE zaten görsel bir `<p>`
                      olarak basar (gerçek bir başlık etiketi DEĞİL) — burada `sr-only` bir
                      `<h3>`, görsel ÇİFTLEME yaratmadan ekran okuyucunun başlık gezinmesine
                      gerçek bir düğüm ekler (görev talimatı: "panel needs a proper heading"). */}
                  <h3 className="sr-only">{t('analysis.review.extractedHeading')}</h3>
                  <AnalysisExtractedFields
                    fields={fields ?? {}}
                    derived={derived ?? null}
                    derivedSourceOk={derivedSourceOk ?? false}
                    culture={culture ?? ''}
                    droppedCount={droppedCount ?? null}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </Modal>
    </div>
  );
}
